// SuggestedPostFeed.jsx
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';

import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';
import UniversalComponent from '../component/UniversalComponent.jsx';
import Loading from '../component/Loading.jsx';

import objectifyArrayById from '../resource/objectifyArrayById.js';
import { NULL_FUNCTION, EMPTY_ARRAY } from '../resource/defaults.js';
import { LoadMode, getPreloadOffset } from '../resource/playerConfig.js';

import media from '../style/media.js';

const OBSERVER_OPTIONS = {
  triggerOnce: true,
  rootMargin: '150px',
};

const SuggestedPostFeed = ({
  meId = null,
  feedName = null,
  nextPage = null,
  prefetchedMessageAmount = 2,
  playerPoolSize = 1,
  isFirstPageFetching = false,
  isNextPageFetching = false,
  currentAutoplayPostId = '',
  postIds = EMPTY_ARRAY,
  fetchFeeds = NULL_FUNCTION,
  setMessageHeightInFeed = NULL_FUNCTION,
}) => {
  const { ref, inView: isIntersected } = useInView(OBSERVER_OPTIONS);
  const resizeObserverRef = useRef(null);
  const currentAutoplayPostIndex = postIds.indexOf(currentAutoplayPostId);
  const preloadOffset = getPreloadOffset(playerPoolSize);

  useEffect(() => {
    if (isIntersected) {
      resizeObserverRef.current = new ResizeObserver(entries => {
        const messageObject = objectifyArrayById({
          array: entries.map(entry => ({
            id: entry.target.getAttribute('data-message_id'),
            messageHeight: entry.target.clientHeight,
          })),
        });
        return setMessageHeightInFeed({
          feedName,
          messageObject,
        });
      });
    }
    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [feedName, isIntersected, setMessageHeightInFeed]);
  useEffect(() => {
    if (feedName && isIntersected && !postIds.length && !isFirstPageFetching) {
      fetchFeeds({
        page: 1,
        type: feedName,
      });
    }
  }, [meId, feedName, isIntersected, isFirstPageFetching, postIds, fetchFeeds]);
  return (
    <StyledSuggestedPostFeed ref={ref}>
      <InfiniteScroller
        useWindow
        pageStart={1}
        hasMore={nextPage != null}
        loadMore={() =>
          fetchFeeds({
            page: nextPage,
            type: feedName,
          })
        }
        isFetching={isFirstPageFetching || isNextPageFetching}
      >
        <Posts>
          <ScrollItem
            loader={
              <PostWrapper>
                <Loading size={24} />
              </PostWrapper>
            }
          >
            {postIds.map((messageId, index) => {
              return (
                <PostWrapper
                  key={index}
                  data-message_id={messageId}
                  ref={element => {
                    if (element && resizeObserverRef.current) {
                      resizeObserverRef.current.unobserve(element);
                      resizeObserverRef.current.observe(element);
                    }
                  }}
                >
                  <UniversalComponent
                    filename="Container_Post"
                    messageId={messageId}
                    shouldPrefetch={
                      // If there is any post has been intersected (currentAutoplayPostIndex >= 0),
                      // prefetch the segments for intersected one and the next two (if any)
                      currentAutoplayPostIndex >= 0 &&
                      index >= currentAutoplayPostIndex &&
                      index <=
                        currentAutoplayPostIndex + prefetchedMessageAmount
                    }
                    loadMode={
                      index === currentAutoplayPostIndex
                        ? LoadMode.ACTIVE
                        : currentAutoplayPostIndex >= 0 &&
                            Math.abs(index - currentAutoplayPostIndex) <=
                              preloadOffset
                          ? LoadMode.PRELOAD
                          : LoadMode.NONE
                    }
                    shouldShowSubscribeAndUnlock
                    messageHeightFeedName={feedName}
                    importer={
                      /* #__LOADABLE__ */ () => import('../container/Post.js')
                    }
                  />
                </PostWrapper>
              );
            })}
          </ScrollItem>
        </Posts>
      </InfiniteScroller>
    </StyledSuggestedPostFeed>
  );
};

SuggestedPostFeed.propTypes = {
  meId: PropTypes.string,
  feedName: PropTypes.string,
  currentAutoplayPostId: PropTypes.string,
  postIds: PropTypes.array,
  nextPage: PropTypes.number,
  prefetchedMessageAmount: PropTypes.number,
  playerPoolSize: PropTypes.number,
  isFirstPageFetching: PropTypes.bool,
  isNextPageFetching: PropTypes.bool,
  fetchFeeds: PropTypes.func,
  setMessageHeightInFeed: PropTypes.func,
};

const StyledSuggestedPostFeed = styled.div``;

const Posts = styled.div``;

const PostWrapper = styled.div`
  margin-bottom: 74px;
  ${media.mobile`
    margin-bottom: 32px;
  `};
`;

export { SuggestedPostFeed };
export default SuggestedPostFeed;
